import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';

import { motion } from 'framer-motion';
import { usePageTransition } from '../../utils/pageTransitions';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import PropertyCard from './components/PropertyCard';
import FilterPanel from './components/FilterPanel';
import PropertyComparison from './components/PropertyComparison';
import InvestmentCalculator from './components/InvestmentCalculator';
import NeighborhoodInfo from './components/NeighborhoodInfo';
import VirtualTourModal from './components/VirtualTourModal';
import PropertyCheckoutModal from './components/PropertyCheckoutModal';
import { propertyService } from '../../services/propertyService';
import Button from '../../components/ui/Button';


const RealEstatePortal = () => {
  const { variants, transition } = usePageTransition();
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showVirtualTour, setShowVirtualTour] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [comparisonList, setComparisonList] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [favorites, setFavorites] = useState([]);
  const [showNeighborhood, setShowNeighborhood] = useState(false);
  const [filters, setFilters] = useState({
    propertyType: 'all',
    category: 'all',
    priceRange: [0, 10000000],
    bedrooms: 'all',
    location: 'all'
  });
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutProperty, setCheckoutProperty] = useState(null);

  useEffect(() => {
    loadProperties();

    // Subscribe to real-time changes
    const unsubscribe = propertyService?.subscribeToChanges((payload) => {
      if (payload?.eventType === 'INSERT') {
        setProperties(prev => [convertProperty(payload?.new), ...prev]);
      } else if (payload?.eventType === 'UPDATE') {
        setProperties(prev => prev?.map(prop =>
          prop?.id === payload?.new?.id ? convertProperty(payload?.new) : prop
        ));
      } else if (payload?.eventType === 'DELETE') {
        setProperties(prev => prev?.filter(prop => prop?.id !== payload?.old?.id));
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    setFilteredProperties(properties);
  }, [properties]);

  const loadProperties = async () => {
    try {
      setLoading(true);
      const data = await propertyService?.getAll();
      setProperties(data);
      setFilteredProperties(data);
    } catch (error) {
      console.error('Error loading properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const convertProperty = (prop) => ({
    id: prop?.id,
    title: prop?.title,
    image: prop?.image,
    imageAlt: prop?.image_alt,
    type: prop?.property_type,
    category: prop?.category,
    location: prop?.location,
    bedrooms: prop?.bedrooms,
    bathrooms: prop?.bathrooms,
    area: prop?.area,
    price: prop?.price,
    priceUSD: prop?.price_usd,
    status: prop?.status,
    featured: prop?.featured,
    coordinates: { lat: prop?.latitude, lng: prop?.longitude },
    isFavorite: false
  });

  const handleFilter = (filters) => {
    let filtered = [...properties];

    if (filters?.category && filters?.category !== 'all') {
      filtered = filtered?.filter(p => p?.category === filters?.category);
    }

    if (filters?.propertyType && filters?.propertyType !== 'all') {
      filtered = filtered?.filter(p => p?.type === filters?.propertyType);
    }

    if (filters?.location && filters?.location !== 'all') {
      filtered = filtered?.filter(p => p?.location === filters?.location);
    }

    if (filters?.priceRange) {
      filtered = filtered?.filter(p => {
        const price = p?.priceUSD;
        return price >= filters?.priceRange?.[0] && price <= filters?.priceRange?.[1];
      });
    }

    if (filters?.bedrooms) {
      filtered = filtered?.filter(p => p?.bedrooms >= filters?.bedrooms);
    }

    setFilteredProperties(filtered);
  };

  const handleResetFilters = () => {
    const resetFilters = {
      propertyType: 'all',
      category: 'all',
      priceRange: [0, 10000000],
      bedrooms: 'all',
      location: 'all'
    };
    setFilters(resetFilters);
    setFilteredProperties(properties);
  };

  const toggleFavorite = (propertyId) => {
    const updatedProperties = properties?.map((p) =>
      p?.id === propertyId ? { ...p, isFavorite: !p?.isFavorite } : p
    );
    setProperties(updatedProperties);
    setFilteredProperties(updatedProperties?.filter((p) => {
      return applyCurrentFilters(p);
    }));

    const property = updatedProperties?.find((p) => p?.id === propertyId);
    if (property?.isFavorite) {
      setFavorites([...favorites, property]);
    } else {
      setFavorites(favorites?.filter((f) => f?.id !== propertyId));
    }
  };

  const applyCurrentFilters = (property) => {
    if (filters?.propertyType !== 'all' && property?.type?.toLowerCase() !== filters?.propertyType) return false;
    if (filters?.category !== 'all' && property?.category?.toLowerCase() !== filters?.category) return false;
    if (filters?.location !== 'all' && property?.location?.toLowerCase() !== filters?.location) return false;
    if (filters?.status !== 'all' && property?.status !== filters?.status) return false;
    if (filters?.maxPrice && property?.price > parseInt(filters?.maxPrice)) return false;
    if (filters?.minArea && property?.area < parseInt(filters?.minArea)) return false;
    if (filters?.maxArea && property?.area > parseInt(filters?.maxArea)) return false;
    return true;
  };

  const addToComparison = (property) => {
    if (comparisonList?.length >= 3) {
      alert('Vous pouvez comparer jusqu\'à 3 propriétés à la fois');
      return;
    }
    if (!comparisonList?.find((p) => p?.id === property?.id)) {
      setComparisonList([...comparisonList, property]);
    }
  };

  const removeFromComparison = (propertyId) => {
    setComparisonList(comparisonList?.filter((p) => p?.id !== propertyId));
  };

  const openVirtualTour = (property) => {
    setSelectedProperty(property);
    setShowVirtualTour(true);
  };

  const openNeighborhoodInfo = (property) => {
    setSelectedProperty(property);
    setShowNeighborhood(true);
  };

  const handleViewDetails = (property) => {
    setSelectedProperty(property);
  };

  const handleVirtualTour = (property) => {
    openVirtualTour(property);
  };

  const handleAddToComparison = (property) => {
    addToComparison(property);
  };

  const handleCheckout = (property) => {
    setCheckoutProperty(property);
    setShowCheckout(true);
  };

  const handleCheckoutComplete = () => {
    setShowCheckout(false);
    setCheckoutProperty(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-white dark:bg-slate-900">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
            <p className="text-slate-500 dark:text-slate-400">Chargement des propriétés...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-slate-900">
      <Helmet>
        <title>Immobilier Premium en RDC - Vente, Location et Gestion | ORAZEK</title>
        <meta name="description" content="Trouvez votre propriété idéale en RDC. Appartements de luxe, villas, bureaux et terrains. Service de gestion et d'évaluation immobilière professionnelle." />
      </Helmet>
      <Header />
      <motion.main
        className="flex-1"
        initial="initial"
        animate="animate"
        exit="exit"
        variants={variants}
        transition={transition}
      >
        {/* Hero Section */}
        <section className="relative py-20 md:py-28 lg:py-32 overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
          {/* Animated blob backgrounds */}
          <motion.div
            className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-blue-500/15 blur-[150px]"
            animate={{ scale: [1, 1.15, 1], opacity: [0.15, 0.25, 0.15] }}
            transition={{ duration: 10, repeat: Infinity }}
          />
          <motion.div
            className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-amber-500/10 blur-[120px]"
            animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
            transition={{ duration: 8, repeat: Infinity, delay: 2 }}
          />

          {/* Noise texture overlay */}
          <div className="absolute inset-0 opacity-10 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] brightness-100 contrast-150 mix-blend-overlay"></div>

          <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16 relative z-10">
            <motion.div
              className="text-center max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/15 rounded-full px-5 py-2.5 mb-6">
                <Icon name="Building2" size={18} className="text-blue-400" />
                <span className="text-sm font-medium text-blue-200">Portail Immobilier Premium</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-6">
                Trouvez Votre{' '}
                <span className="bg-gradient-to-r from-blue-400 to-amber-400 bg-clip-text text-transparent">
                  Propriété Idéale
                </span>
              </h1>
              <p className="text-xl text-blue-100/70 mb-10 max-w-3xl mx-auto leading-relaxed">
                Découvrez notre sélection exclusive de propriétés premium à Kinshasa
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
                  <Button
                    variant="default"
                    size="lg"
                    iconName="Calculator"
                    iconPosition="left"
                    onClick={() => setShowCalculator(true)}
                    className="bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/25 border-none rounded-xl px-8 py-3"
                  >
                    Calculateur d'investissement
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
                  <Button
                    variant="outline"
                    size="lg"
                    iconName="MessageCircle"
                    iconPosition="left"
                    onClick={() => window.open('https://wa.me/243818883531', '_blank')}
                    className="border-white/20 text-white hover:bg-white/5 backdrop-blur-sm rounded-xl px-8 py-3"
                  >
                    Contacter un agent
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Properties Section */}
        <section className="py-12 md:py-16 lg:py-20">
          <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16">
            <motion.div
              className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-2">
                  Propriétés disponibles
                </h2>
                <p className="text-slate-600 dark:text-slate-400">
                  {filteredProperties?.length} propriété{filteredProperties?.length > 1 ? 's' : ''} trouvée{filteredProperties?.length > 1 ? 's' : ''}
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                {favorites?.length > 0 &&
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Heart"
                    iconPosition="left"
                    onClick={() => setFilteredProperties(favorites)}
                    className="border-slate-200 dark:border-white/20 text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-white/5"
                  >
                    Favoris ({favorites?.length})
                  </Button>
                }
                {comparisonList?.length > 0 &&
                  <Button
                    variant="default"
                    size="sm"
                    iconName="GitCompare"
                    iconPosition="left"
                    onClick={() => setShowComparison(true)}
                    className="bg-blue-600 hover:bg-blue-500 text-white border-none"
                  >
                    Comparer ({comparisonList?.length})
                  </Button>
                }
              </div>
            </motion.div>

            <FilterPanel
              filters={filters}
              onFilterChange={handleFilter}
              onReset={handleResetFilters}
            />

            <div className={`${viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8' : 'flex flex-col gap-4'}`}>
              {filteredProperties?.map((property, index) => (
                <PropertyCard
                  key={property?.id}
                  property={property}
                  onToggleFavorite={toggleFavorite}
                  onCheckout={handleCheckout}
                  onViewDetails={handleViewDetails}
                  onVirtualTour={handleVirtualTour}
                  onCompare={handleAddToComparison}
                  viewMode={viewMode}
                />
              ))}
            </div>

            {filteredProperties?.length === 0 && !loading &&
              <motion.div
                className="text-center py-20"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="w-24 h-24 rounded-2xl bg-white/80 dark:bg-slate-800/60 border border-slate-200 dark:border-white/10 flex items-center justify-center mx-auto mb-6">
                  <Icon name="Home" size={40} className="text-slate-500" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white mb-3">
                  Aucune propriété trouvée
                </h3>
                <p className="text-slate-600 dark:text-slate-400 mb-8">
                  Essayez d'ajuster vos filtres de recherche
                </p>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
                  <Button
                    variant="default"
                    iconName="RotateCcw"
                    iconPosition="left"
                    onClick={handleResetFilters}
                    className="bg-blue-600 hover:bg-blue-500 text-white border-none rounded-xl px-8 py-3"
                  >
                    Réinitialiser les filtres
                  </Button>
                </motion.div>
              </motion.div>
            }
          </div>
        </section>

        {/* Neighborhood Section */}
        {showNeighborhood && selectedProperty &&
          <section className="py-12 md:py-16 lg:py-20">
            <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16">
              <NeighborhoodInfo
                location={selectedProperty?.location}
                coordinates={selectedProperty?.coordinates}
              />
            </div>
          </section>
        }

        {/* Features Section */}
        <section className="py-16 md:py-20 lg:py-28 relative overflow-hidden">
          {/* Background elements */}
          <div
            className="absolute inset-0 opacity-[0.03] pointer-events-none"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'repeat',
            }}
          />
          <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-blue-600/5 rounded-full blur-[120px] pointer-events-none" />
          <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-amber-500/5 rounded-full blur-[100px] pointer-events-none" />

          <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16 relative z-10">
            <motion.div
              className="text-center mb-12 md:mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.7 }}
            >
              <div className="inline-flex items-center gap-2 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full px-5 py-2.5 mb-6">
                <Icon name="Star" size={18} className="text-amber-400" />
                <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Nos Avantages</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-6xl font-bold text-slate-900 dark:text-white mb-5">
                Pourquoi choisir{' '}
                <span className="bg-gradient-to-r from-blue-400 to-amber-400 bg-clip-text text-transparent">
                  ORAZEK Investment?
                </span>
              </h2>
              <p className="text-xl text-slate-600 dark:text-slate-400 max-w-3xl mx-auto leading-relaxed">
                Votre partenaire de confiance pour l'immobilier à Kinshasa
              </p>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-60px' }}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: { staggerChildren: 0.12, delayChildren: 0.1 }
                }
              }}
            >
              {[
                {
                  icon: 'Shield',
                  title: 'Transactions sécurisées',
                  description: 'Processus transparent et conforme aux normes DRC'
                },
                {
                  icon: 'Users',
                  title: 'Agents experts',
                  description: 'Équipe professionnelle avec connaissance locale approfondie'
                },
                {
                  icon: 'TrendingUp',
                  title: 'Meilleur ROI',
                  description: "Propriétés sélectionnées pour leur potentiel d'investissement"
                },
                {
                  icon: 'Headphones',
                  title: 'Support 24/7',
                  description: 'Assistance continue via WhatsApp et téléphone'
                }
              ]?.map((feature, index) => (
                <motion.div
                  key={index}
                  variants={{
                    hidden: { opacity: 0, y: 40 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
                  }}
                  className="bg-white/80 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-3xl p-8 md:p-10 hover:border-blue-500/30 shadow-2xl transition-all duration-300 group"
                >
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-blue-900/30">
                    <Icon name={feature?.icon} size={28} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                    {feature?.title}
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                    {feature?.description}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-20 lg:py-28 relative overflow-hidden">
          <motion.div
            className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-blue-600/10 blur-3xl"
            animate={{ x: [0, 40, 0], y: [0, -30, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-amber-500/8 blur-3xl"
            animate={{ x: [0, -40, 0], y: [0, 30, 0], scale: [1, 1.15, 1] }}
            transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
          />

          <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16 relative z-10">
            <motion.div
              className="bg-gradient-to-r from-blue-900/30 to-slate-800/40 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-[2.5rem] p-10 md:p-16 lg:p-20 text-center"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.7 }}
            >
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white mb-6">
                Prêt à trouver votre{' '}
                <span className="bg-gradient-to-r from-blue-400 to-amber-400 bg-clip-text text-transparent">
                  propriété idéale?
                </span>
              </h2>
              <p className="text-xl text-slate-600 dark:text-slate-400 mb-10 max-w-2xl mx-auto leading-relaxed">
                Contactez nos agents immobiliers pour une consultation personnalisée
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
                  <Button
                    variant="default"
                    size="lg"
                    iconName="Calendar"
                    iconPosition="left"
                    className="bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/25 border-none w-full sm:w-auto rounded-xl px-8 py-3"
                  >
                    Planifier une visite
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
                  <Button
                    variant="outline"
                    size="lg"
                    iconName="Phone"
                    iconPosition="left"
                    className="border-slate-200 dark:border-white/20 text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-white/5 backdrop-blur-sm w-full sm:w-auto rounded-xl px-8 py-3"
                  >
                    Appeler maintenant
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>
      </motion.main>
      <Footer />
      <VirtualTourModal
        property={selectedProperty}
        isOpen={showVirtualTour}
        onClose={() => setShowVirtualTour(false)}
      />
      <InvestmentCalculator
        isOpen={showCalculator}
        onClose={() => setShowCalculator(false)}
      />
      <PropertyComparison
        properties={comparisonList}
        isOpen={showComparison}
        onClose={() => setShowComparison(false)}
        onRemove={removeFromComparison}
      />
      <PropertyCheckoutModal
        isOpen={showCheckout}
        onClose={() => setShowCheckout(false)}
        property={checkoutProperty}
        onComplete={handleCheckoutComplete}
      />
      <button
        onClick={() => window.open('https://wa.me/243818883531', '_blank')}
        className="fixed bottom-6 right-6 w-14 h-14 md:w-16 md:h-16 rounded-full bg-green-500 text-white shadow-2xl shadow-green-500/25 hover:shadow-green-500/40 transition-all hover:scale-110 flex items-center justify-center z-40"
        aria-label="Contact via WhatsApp"
      >
        <Icon name="MessageCircle" size={28} />
      </button>
    </div>
  );

};

export default RealEstatePortal;
